import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { EmployeePageContext } from './EmployeePage';
import { useEffect, useState } from 'react';
import { SalaryDeduction } from '../../types/SalaryDeduction';
import { querySalaryDeduction } from '../../utils/querySalaryDeductions';
import { AddRounded } from '@mui/icons-material';
import { SecureComponent } from '../../security/SecureComponent';
import { VacationRequestForm } from '../vacation/VacationRequestForm';
import { useSnackbar } from 'notistack';
import { createSalaryDeduction } from '../../utils/createSalaryDeduction';
import { getErrorString } from '../../utils/getErrorString';

interface SalaryDeductionBoxProps {

}
export const SalaryDeductionBox = (prop: SalaryDeductionBoxProps) => {
  const { employee }: EmployeePageContext = useOutletContext();
  const { enqueueSnackbar } = useSnackbar()

  const [salaryDeductions, setSalaryDeductions] = useState<SalaryDeduction[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [numDays, setNumDays] = useState(0);
  const [reason, setReason] = useState("");

  const refresh = () => {
    querySalaryDeduction(employee.idNumber)
      .then(result => {
        setSalaryDeductions(result);
      });
  };

  const handleSaveOnClick = () => {
    if (numDays === 0) {
      // Enter value for number of days
      enqueueSnackbar("Please enter a number of days to be deducted.", { variant: "error" });
      return;
    }

    if (reason.trim() === "") {
      // Enter value for salary deduction reason
      enqueueSnackbar("Please enter the reason of salary deduction", { variant: "error" });
      return;
    }

    createSalaryDeduction(employee, numDays, reason)
      .then(() => {
        enqueueSnackbar("Salary deduction has been submitted!", { variant: "success" });
        setNumDays(0);
        setReason("");
        setShowForm(false);
        refresh();
      })
      .catch((error) => {
        enqueueSnackbar(getErrorString(error), { variant: "error" });
      });
  };

  useEffect(() => {
    if (employee) {
      refresh();
    }
  }, [employee]);

  return (
    <Stack>
      <SecureComponent permissions={['create.deductionSalary']}>
        <Stack direction='row-reverse' mb={1}>
          <Button startIcon={<AddRounded />} onClick={() => { setShowForm(true); }}>Add Salary Deduction</Button>
        </Stack>
      </SecureComponent>
      <SecureComponent permissions={['read.deductionSalary']}>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell width={120}>Id Number</TableCell>
                  <TableCell width={180}>Name</TableCell>
                  <TableCell width={220}>Date</TableCell>
                  <TableCell width={120} align="center">No. of Days</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salaryDeductions.length === 0 && (
                  <TableRow
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 } 
                    }}>
                    <TableCell colSpan={6}><Typography variant="caption">No salary deductions to show...</Typography></TableCell>
                  </TableRow>
                )}
                {salaryDeductions.map((v, i) => {
                  return (
                    <TableRow
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 } 
                      }}>
                      <TableCell  align="right">{i + 1}</TableCell>
                      <TableCell>{v.idNumber}</TableCell>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>{v.date.toFormat("MMMM dd, yyyy")}</TableCell>
                      <TableCell align="center">{v.numDays}</TableCell>
                      <TableCell>{v.reason}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </SecureComponent>
      <SecureComponent permissions={['create.deductionSalary']}>
        <Dialog open={showForm} onClose={() => setShowForm(false)} fullWidth>
          <DialogTitle>Add Salary Deduction</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={4}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar src={employee.picture} />
                <Stack>
                  <Typography fontWeight="bold">{employee.name}</Typography>
                  <Typography variant="caption">{employee.idNumber}</Typography>
                </Stack>
              </Stack>
              <Stack spacing={2}>
                <TextField 
                  inputMode="numeric" 
                  inputProps={{ inputMode: "numeric" }}
                  label="Number of days"
                  placeholder="Please enter the number of days to be deducted"
                  value={numDays}
                  onChange={(e) => {
                    let val = +e.target.value;
                    if (!isNaN(val)) {
                      setNumDays(val);
                    }
                  }}
                />
                <TextField
                  label="Reason"
                  placeholder="Please enter the reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  multiline
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={() => setShowForm(false)}>CANCEL</Button>
            <Button color="primary" onClick={handleSaveOnClick}>SAVE</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
    </Stack>
  );
};
